import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, forkJoin, from, throwError, timer } from 'rxjs';
import { map, catchError, switchMap, retry, tap, take } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { KiwixiGlobals } from '../kiwixi/kiwixi.globals';
import { KiwixiService } from '../kiwixi/kiwixi.service';
import { MaterialService } from './material.service';
import { EventService } from './event.service';
import { SearchPlanSolution } from '../model/searchPlanSolution.model';
import { Formation } from '../model/formation.model';
import { Event } from '../model/event.model';
import { getPlanErrorMessage } from '../utils/plan-result.util';

/**
 * Statuts possibles de la tâche Celery
 */
type PlanTaskStatus = 'INIT' | 'PROGRESS' | 'SUCCESS' | 'FAILURE';

/**
 * Réponse API Plan (structure Celery task)
 */
interface PlanResponse {
  status: PlanTaskStatus;
  result: string | any; // JSON stringifié ou objet direct
  task_id?: string;
}

/**
 * Paramètres pour les algorithmes de planification
 */
interface PlanAlgorithmParams {
  algorithm: string;
  formation?: number;
  teacher?: number;
  event?: number;
  month?: number;
  year?: number;
  open_to_all_teacher?: boolean;
  force_teachers?: number[];
  exclude_teachers?: number[];
  timezone?: string;
  day?: number;
  start?: string;
  simulation?: boolean;
}

/**
 * Service de gestion de la planification
 * 
 * Responsabilités:
 * - Disponibilités des professeurs
 * - Génération de plannings
 * - Réservations ponctuelles
 * - Recherche de solutions de planification
 * 
 * Version modernisée:
 * - RxJS Observables au lieu de Promises
 * - Gestion d'erreurs centralisée
 * - Retry automatique pour tâches asynchrones
 * - Types stricts
 * - Pas de subscribe interne
 */
@Injectable()
export class PlanServiceNew extends KiwixiService {
  private readonly apiPlan = `${environment.server}plan/`;
  private readonly apiLogEntry = `${environment.server}planlog/`;
  
  // Retry configuration pour polling Celery
  private readonly MAX_RETRIES = 30;
  private readonly RETRY_DELAY_MS = 1000;

  constructor(
    http: HttpClient,
    router: Router,
    kiwixiGlobals: KiwixiGlobals,
    private eventService: EventService,
    private materialService: MaterialService
  ) {
    super(http, router, kiwixiGlobals, materialService);
  }

  /**
   * ============================================
   * MÉTHODES PRINCIPALES - OBSERVABLES
   * ============================================
   */

  /**
   * Exécute un algorithme de planification (POST + polling)
   * @param params Paramètres de l'algorithme
   * @returns Observable avec le résultat final
   */
  executePlanAlgorithm(params: PlanAlgorithmParams): Observable<any> {
    const headers = this.createHttpClientAuthorizationHeader();
    
    return this.http.post<string>(this.apiPlan, params, { headers }).pipe(
      switchMap(taskId => this.pollPlanTask(taskId)),
      catchError(this.handleErrorObservable<any>('executePlanAlgorithm'))
    );
  }

  /**
   * Polling d'une tâche Celery jusqu'à SUCCESS ou FAILURE
   * @param taskId ID de la tâche (peut être une URL ou un ID)
   * @returns Observable avec le résultat final
   */
  private pollPlanTask(taskId: string | any, retryCount = 0): Observable<any> {
    const headers = this.createHttpClientAuthorizationHeader();
    const endpoint = typeof taskId === 'string' ? `${this.apiPlan}${taskId}` : `${this.apiPlan}${taskId}`;
    
    return this.http.get<PlanResponse>(endpoint, { headers }).pipe(
      switchMap(response => {
        // Cas de succès
        if (response.status === 'SUCCESS') {
          return this.parsePlanResult(response.result);
        }
        
        // Cas d'échec
        if (response.status === 'FAILURE') {
          const errorData = this.parsePlanResult(response.result);
          return throwError(() => new Error(`Plan algorithm failed: ${JSON.stringify(errorData)}`));
        }
        
        // Cas en cours (INIT, PROGRESS) - retry après délai
        if (retryCount >= this.MAX_RETRIES) {
          return throwError(() => new Error('Plan algorithm timeout: max retries exceeded'));
        }
        
        return timer(this.RETRY_DELAY_MS).pipe(
          switchMap(() => this.pollPlanTask(taskId, retryCount + 1))
        );
      }),
      catchError(error => {
        console.error('Error polling plan task:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Parse le résultat de l'API (peut être JSON stringifié)
   */
  private parsePlanResult(result: string | any): Observable<any> {
    try {
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      return from([parsed]);
    } catch (error) {
      return from([result]);
    }
  }

  /**
   * ============================================
   * DISPONIBILITÉS - OBSERVABLES
   * ============================================
   */

  /**
   * Récupère les jours disponibles pour une formation
   * @param formationId ID de la formation
   * @param openToAllTeachers Ouvrir à tous les profs
   * @param forceTeachers Liste de profs forcés
   * @param excludeTeachers Liste de profs exclus
   * @returns Observable<number[]> Liste des jours disponibles (0-6)
   */
  getAvailableDays(
    formationId: number,
    openToAllTeachers = false,
    forceTeachers: number[] = [],
    excludeTeachers: number[] = []
  ): Observable<number[]> {
    const params: PlanAlgorithmParams = {
      algorithm: 'availabilityteachers',
      formation: formationId,
      open_to_all_teacher: openToAllTeachers,
      force_teachers: forceTeachers,
      exclude_teachers: excludeTeachers,
    };
    
    return this.executePlanAlgorithm(params).pipe(
      map(data => Array.isArray(data) ? data : []),
      catchError(error => {
        console.error('Error getting available days:', error);
        return from([[]]);
      })
    );
  }

  /**
   * Récupère les heures de début disponibles pour un jour
   * @param formationId ID de la formation
   * @param dayId Jour (0-6)
   * @param openToAllTeachers Ouvrir à tous les profs
   * @param forceTeachers Liste de profs forcés
   * @param excludeTeachers Liste de profs exclus
   * @returns Observable<any[]> Liste des créneaux disponibles
   */
  getAvailableStarts(
    formationId: number,
    dayId: number,
    openToAllTeachers = false,
    forceTeachers: number[] = [],
    excludeTeachers: number[] = []
  ): Observable<any[]> {
    const params: PlanAlgorithmParams = {
      algorithm: 'availabilityteachers',
      formation: formationId,
      day: dayId,
      open_to_all_teacher: openToAllTeachers,
      force_teachers: forceTeachers,
      exclude_teachers: excludeTeachers,
    };
    
    return this.executePlanAlgorithm(params).pipe(
      map(data => Array.isArray(data) ? data : []),
      catchError(error => {
        console.error('Error getting available starts:', error);
        return from([[]]);
      })
    );
  }

  /**
   * Récupère les heures de fin disponibles pour un jour et une heure de début
   * @param formationId ID de la formation
   * @param dayId Jour (0-6)
   * @param startTime Heure de début (ISO string)
   * @param openToAllTeachers Ouvrir à tous les profs
   * @param forceTeachers Liste de profs forcés
   * @param excludeTeachers Liste de profs exclus
   * @returns Observable<any[]> Liste des créneaux de fin disponibles
   */
  getAvailableEnds(
    formationId: number,
    dayId: number,
    startTime: string,
    openToAllTeachers = false,
    forceTeachers: number[] = [],
    excludeTeachers: number[] = []
  ): Observable<any[]> {
    const params: PlanAlgorithmParams = {
      algorithm: 'availabilityteachers',
      formation: formationId,
      day: dayId,
      start: startTime,
      open_to_all_teacher: openToAllTeachers,
      force_teachers: forceTeachers,
      exclude_teachers: excludeTeachers,
    };
    
    return this.executePlanAlgorithm(params).pipe(
      map(data => Array.isArray(data) ? data : []),
      catchError(error => {
        console.error('Error getting available ends:', error);
        return from([[]]);
      })
    );
  }

  /**
   * Récupère toutes les disponibilités (legacy, à éviter)
   * @deprecated Utiliser getAvailableDays, getAvailableStarts, getAvailableEnds
   */
  getDisponibilities(formationUrl: string, algoVersion = 'avaibilityHour', open = false): Observable<any> {
    const params: PlanAlgorithmParams = {
      algorithm: algoVersion,
      formation: this.kiwixiGlobals.getIdFromUrl(formationUrl),
      open_to_all_teacher: open,
    };
    
    return this.executePlanAlgorithm(params);
  }

  /**
   * ============================================
   * PROFESSEURS
   * ============================================
   */

  /**
   * Récupère les professeurs disponibles pour une formation
   */
  getTeachersAvailabilities(formationUrl: string, simulation = false): Observable<any> {
    const params: PlanAlgorithmParams = {
      algorithm: 'availabilityteacher',
      simulation: simulation,
      formation: this.kiwixiGlobals.getIdFromUrl(formationUrl),
    };
    
    return this.executePlanAlgorithm(params);
  }

  /**
   * Récupère les disponibilités d'un professeur spécifique
   */
  getTeacherDisponibilities(teacherUrl: string, month?: number, year?: number): Observable<any> {
    const now = new Date();
    const params: PlanAlgorithmParams = {
      algorithm: 'availabilityteacher',
      teacher: this.kiwixiGlobals.getIdFromUrl(teacherUrl),
      month: month || now.getMonth() + 1,
      year: year || now.getFullYear(),
    };
    
    return this.executePlanAlgorithm(params);
  }

  /**
   * Récupère les professeurs disponibles pour un event
   */
  getTeacherForEvent(eventUrl: string, open = false): Observable<any> {
    const params: PlanAlgorithmParams = {
      algorithm: 'availabilityteachers',
      event: this.extractIdFromUrl(eventUrl),
      open_to_all_teacher: open,
    };
    
    return this.executePlanAlgorithm(params);
  }

  /**
   * Récupère les professeurs disponibles pour plusieurs events
   */
  getTeachersForMultipleEvents(events: Event[]): Observable<any[]> {
    const requests = events.map(event => {
      const params: PlanAlgorithmParams = {
        algorithm: 'availabilityteachers',
        event: event.id,
        open_to_all_teacher: true,
      };
      return this.executePlanAlgorithm(params);
    });
    
    return forkJoin(requests);
  }

  /**
   * ============================================
   * GÉNÉRATION DE PLANNING
   * ============================================
   */

  /**
   * Génère un planning pour une formation
   * @param formation Formation à planifier
   * @returns Observable avec les heures restantes
   */
  generatePlanning(formation: Formation): Observable<number> {
    // 1. Sauvegarder tous les events
    const saveObservables = formation.events
      .filter(event => event.start && event.end)
      .map(event => this.eventService.saveEvent(event));
    
    const saveAll$ = saveObservables.length > 0 ? forkJoin(saveObservables) : from([null]);
    
    return saveAll$.pipe(
      switchMap(() => {
        // 2. Créer le planning
        const params: PlanAlgorithmParams = {
          algorithm: 'writeCalendar',
          formation: this.kiwixiGlobals.getIdFromUrl(formation.url),
        };
        
        return this.executePlanAlgorithm(params);
      }),
      tap(() => {
        this.materialService.openSnackBar('Planning généré', 'green');
      }),
      map(result => result as number),
      catchError(error => {
        console.error('Error generating planning:', error);
        this.materialService.openSnackBar('Erreur génération planning', 'red');
        return throwError(() => error);
      })
    );
  }

  /**
   * Génère une simulation de planning
   */
  generateSimulation(formation: Formation): Observable<number> {
    const saveObservables = formation.events
      .filter(event => event.start && event.end)
      .map(event => this.eventService.saveEvent(event));
    
    const saveAll$ = saveObservables.length > 0 ? forkJoin(saveObservables) : from([null]);
    
    return saveAll$.pipe(
      switchMap(() => {
        const params: PlanAlgorithmParams = {
          algorithm: 'writeCalendar',
          formation: formation.id,
        };
        
        return this.executePlanAlgorithm(params);
      }),
      tap(() => {
        this.materialService.openSnackBar('Simulation générée', 'green');
      }),
      catchError(error => {
        console.error('Error generating simulation:', error);
        this.materialService.openSnackBar('Erreur génération simulation', 'red');
        return throwError(() => error);
      })
    );
  }

  /**
   * Écrit une simulation
   */
  writeSimulation(formation: Formation): Observable<any> {
    const params: PlanAlgorithmParams = {
      algorithm: 'writeCalendar',
      formation: formation.id,
    };
    
    return this.executePlanAlgorithm(params).pipe(
      tap(() => {
        this.materialService.openSnackBar('Planning généré', 'green');
      }),
      catchError(error => {
        this.materialService.openSnackBar('Erreur génération planning', 'red');
        return throwError(() => error);
      })
    );
  }

  /**
   * Valide un planning depuis un reschedule
   */
  validPlanningFromReschedule(formation: Formation, algo: string): Observable<any> {
    const params: PlanAlgorithmParams = {
      algorithm: algo,
      formation: this.extractIdFromUrl(formation.url),
    };
    
    return this.executePlanAlgorithm(params).pipe(
      tap(result => {
        if (result === false) {
          this.materialService.openSnackBar('Erreur génération planning', 'red');
        } else {
          this.materialService.openSnackBar('Planning généré', 'green');
        }
      }),
      catchError(error => {
        this.materialService.openSnackBar('Erreur génération planning', 'red');
        return throwError(() => error);
      })
    );
  }

  /**
   * ============================================
   * RÉSERVATION PONCTUELLE
   * ============================================
   */

  /**
   * Génère une réservation ponctuelle (one-time lesson)
   */
  generatePonctualBooking(formation: Formation, newEvent: Event): Observable<any> {
    return from(this.eventService.postEvent(newEvent)).pipe(
      switchMap(createdEvent => {
        const params: PlanAlgorithmParams = {
          algorithm: 'writeonetimelesson',
          formation: this.kiwixiGlobals.getIdFromUrl(formation.url),
          teacher: newEvent.teacher ? this.kiwixiGlobals.getIdFromUrl(newEvent.teacher) : undefined,
          event: createdEvent.id,
        };
        
        const headers = this.createHttpClientAuthorizationHeader();
        return this.http.post(this.apiPlan, params, { observe: 'response', headers }).pipe(
          switchMap(response => {
            if (response.status === 200 && response.body) {
              return this.pollPlanTask(response.body);
            }
            return throwError(() => new Error('Invalid response from API'));
          })
        );
      }),
      tap(result => {
        // La tâche répond 200 même quand elle refuse la réservation : le créneau
        // a pu être pris entre le choix de l'élève et l'écriture.
        const planError = getPlanErrorMessage(result);
        if (planError) {
          this.materialService.openSnackBar(planError, 'red');
        } else {
          this.materialService.openSnackBar('Réservation créée', 'green');
        }
      }),
      catchError(error => {
        console.error('Error creating ponctual booking:', error);
        this.materialService.openSnackBar('Erreur création réservation', 'red');
        return throwError(() => error);
      })
    );
  }

  /**
   * ============================================
   * LOGS
   * ============================================
   */

  /**
   * Récupère les logs d'entrée de planning
   */
  getLogEntry(params: string): Observable<any> {
    const headers = this.createHttpClientAuthorizationHeader();
    return this.http.get<any>(`${this.apiLogEntry}${params}`, { headers }).pipe(
      catchError(this.handleErrorObservable<any>('getLogEntry'))
    );
  }

  /**
   * ============================================
   * SIMULATION
   * ============================================
   */

  /**
   * Réinitialise une simulation (supprime les events)
   */
  resetSimulation(formationId: number): Observable<any> {
    const headers = this.createHttpClientAuthorizationHeader();
    const url = `${environment.server}events_delete/${formationId}/`;
    return this.http.delete(url, { headers }).pipe(
      catchError(this.handleErrorObservable<any>('resetSimulation'))
    );
  }

  /**
   * ============================================
   * RECHERCHE DE SOLUTION (searchsolution algorithm)
   * ============================================
   */

  /**
   * Recherche une solution de planification complète (legacy-compatible)
   * @param params Paramètres complets incluant new_events, events_to_delete, etc.
   * @returns Observable<SearchPlanSolution>
   */
  searchSolution(params: {
    formation: number;
    open_to_all_teacher: boolean;
    algorithm: string;
    allocation_mode: string;
    new_events: Event[];
    events_to_delete: Event[];
    events_to_extend: any[];
    teachers_to_replace: any[];
    simulation: boolean;
    completion_control?: boolean;
  }): Observable<SearchPlanSolution> {
    const headers = this.createHttpClientAuthorizationHeader();
    
    return this.http.post<string>(this.apiPlan, params, { headers }).pipe(
      switchMap(taskId => this.pollPlanTask(taskId)),
      map(result => result as SearchPlanSolution),
      catchError(this.handleErrorObservable<SearchPlanSolution>('searchSolution'))
    );
  }
}
